import { useEffect, useRef, useCallback, useState } from 'react';

interface SectionScrollOptions {
  sections: string[]; // Array of section IDs
  transitionDuration?: number; // Duration of scroll animation in ms
  scrollThreshold?: number; // Minimum scroll delta to trigger section change
}

export const useSectionScroll = ({
  sections,
  transitionDuration = 800,
  scrollThreshold = 50
}: SectionScrollOptions) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrollTime = useRef(0);
  const accumulatedDelta = useRef(0);
  const touchStartY = useRef(0);

  const scrollToSection = useCallback((sectionIndex: number, force = false) => {
    if ((isScrolling && !force) || sectionIndex < 0 || sectionIndex >= sections.length) {
      return;
    }

    setIsScrolling(true);
    const targetSection = document.getElementById(sections[sectionIndex]);
    
    if (targetSection) {
      targetSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setCurrentSection(sectionIndex);

      // Reset scrolling lock after transition
      setTimeout(() => {
        setIsScrolling(false);
        accumulatedDelta.current = 0;
      }, transitionDuration);
    }
  }, [sections, transitionDuration, isScrolling]);

  // Helper function to check if a section needs internal scrolling
  const checkSectionScrollability = useCallback((sectionIndex: number) => {
    const sectionElement = document.getElementById(sections[sectionIndex]);
    if (!sectionElement) return false;
    
    // Check if content overflows viewport (especially important on mobile)
    const sectionHeight = sectionElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const isMobile = window.innerWidth <= 768;
    
    // For mobile, allow scrolling if content exceeds viewport
    // For desktop, maintain existing behavior
    return isMobile && sectionHeight > viewportHeight;
  }, [sections]);

  const handleWheel = useCallback((e: WheelEvent) => {
    const now = Date.now();
    const timeDelta = now - lastScrollTime.current;
    
    // Reset accumulated delta if too much time has passed
    if (timeDelta > 150) {
      accumulatedDelta.current = 0;
    }
    
    lastScrollTime.current = now;
    
    if (isScrolling) return;

    const isLastSection = currentSection === sections.length - 1;
    const isAppSection = currentSection === 1; // App section is the second section (index 1)
    const direction = e.deltaY > 0 ? 1 : -1;
    
    // Special handling for the last section (testimonials) - existing behavior
    if (isLastSection) {
      const currentSectionElement = document.getElementById(sections[currentSection]);
      if (currentSectionElement) {
        const rect = currentSectionElement.getBoundingClientRect();
        const isAtTop = rect.top >= -10; // Small tolerance
        
        // Only prevent default and change sections if at the top and scrolling up
        if (isAtTop && direction === -1) {
          e.preventDefault();
          // Accumulate scroll delta for going back to previous section
          accumulatedDelta.current += Math.abs(e.deltaY);
          
          if (accumulatedDelta.current >= scrollThreshold) {
            scrollToSection(currentSection - 1);
            accumulatedDelta.current = 0;
          }
        }
        // Allow normal scrolling within the testimonials section
        return;
      }
    }
    
    // Special handling for app section on mobile when content overflows
    if (isAppSection && checkSectionScrollability(currentSection)) {
      const currentSectionElement = document.getElementById(sections[currentSection]);
      if (currentSectionElement) {
        const rect = currentSectionElement.getBoundingClientRect();
        const isAtTop = rect.top >= -10;
        const isAtBottom = (window.innerHeight + window.pageYOffset) >= 
          (currentSectionElement.offsetTop + currentSectionElement.offsetHeight - 10);
        
        // Allow scrolling within the app section
        if (direction === -1 && isAtTop) {
          // At top, scrolling up - go to previous section
          e.preventDefault();
          accumulatedDelta.current += Math.abs(e.deltaY);
          
          if (accumulatedDelta.current >= scrollThreshold) {
            scrollToSection(currentSection - 1);
            accumulatedDelta.current = 0;
          }
        } else if (direction === 1 && isAtBottom) {
          // At bottom, scrolling down - go to next section
          e.preventDefault();
          accumulatedDelta.current += Math.abs(e.deltaY);
          
          if (accumulatedDelta.current >= scrollThreshold) {
            scrollToSection(currentSection + 1);
            accumulatedDelta.current = 0;
          }
        }
        // Otherwise, allow normal scrolling within the section
        return;
      }
    }

    // For other sections, prevent default scrolling and use section navigation
    e.preventDefault();
    
    // Accumulate scroll delta
    accumulatedDelta.current += Math.abs(e.deltaY);
    
    // Only trigger section change if we've accumulated enough scroll
    if (accumulatedDelta.current >= scrollThreshold) {
      const nextSection = currentSection + direction;
      
      if (nextSection >= 0 && nextSection < sections.length) {
        scrollToSection(nextSection);
      }
      
      accumulatedDelta.current = 0;
    }
  }, [currentSection, isScrolling, scrollToSection, scrollThreshold, sections, checkSectionScrollability]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isScrolling || e.touches.length !== 1) return;
    
    const touchY = e.touches[0].clientY;
    const deltaY = touchStartY.current - touchY;
    
    const isLastSection = currentSection === sections.length - 1;
    const isAppSection = currentSection === 1; // App section is the second section (index 1)
    const direction = deltaY > 0 ? 1 : -1;
    
    // Special handling for the last section (testimonials) - existing behavior
    if (isLastSection) {
      const currentSectionElement = document.getElementById(sections[currentSection]);
      if (currentSectionElement) {
        const rect = currentSectionElement.getBoundingClientRect();
        const isAtTop = rect.top >= -10; // Small tolerance
        
        // Only prevent default and change sections if at the top and swiping down
        if (isAtTop && direction === -1 && Math.abs(deltaY) > scrollThreshold) {
          e.preventDefault();
          scrollToSection(currentSection - 1);
        }
        // Allow normal touch scrolling within the testimonials section
        return;
      }
    }
    
    // Special handling for app section on mobile when content overflows
    if (isAppSection && checkSectionScrollability(currentSection)) {
      const currentSectionElement = document.getElementById(sections[currentSection]);
      if (currentSectionElement) {
        const rect = currentSectionElement.getBoundingClientRect();
        const isAtTop = rect.top >= -10;
        const isAtBottom = (window.innerHeight + window.pageYOffset) >= 
          (currentSectionElement.offsetTop + currentSectionElement.offsetHeight - 10);
        
        // Allow touch scrolling within the app section
        if (direction === -1 && isAtTop && Math.abs(deltaY) > scrollThreshold) {
          // At top, swiping down - go to previous section
          e.preventDefault();
          scrollToSection(currentSection - 1);
        } else if (direction === 1 && isAtBottom && Math.abs(deltaY) > scrollThreshold) {
          // At bottom, swiping up - go to next section
          e.preventDefault();
          scrollToSection(currentSection + 1);
        }
        // Otherwise, allow normal touch scrolling within the section
        return;
      }
    }

    // For other sections, prevent default scrolling and use section navigation
    e.preventDefault();
    
    // Only trigger if swipe is significant enough
    if (Math.abs(deltaY) > scrollThreshold) {
      const nextSection = currentSection + direction;
      
      if (nextSection >= 0 && nextSection < sections.length) {
        scrollToSection(nextSection);
      }
    }
  }, [currentSection, isScrolling, scrollToSection, scrollThreshold, sections, checkSectionScrollability]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (isScrolling) return;
    
    let nextSection = currentSection;
    const isLastSection = currentSection === sections.length - 1;
    const isAppSection = currentSection === 1; // App section is the second section (index 1)
    
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
      case ' ': // Spacebar
        // In the last section, allow normal page down/space behavior
        if (isLastSection) {
          const currentSectionElement = document.getElementById(sections[currentSection]);
          if (currentSectionElement) {
            const isAtBottom = (window.innerHeight + window.pageYOffset) >= (currentSectionElement.offsetTop + currentSectionElement.offsetHeight - 10);
            
            // Only prevent default if we're at the bottom (nowhere to scroll)
            if (isAtBottom) {
              e.preventDefault();
            }
            return; // Allow normal scrolling within testimonials
          }
        }
        // In the app section on mobile, allow normal page down/space behavior if content overflows
        if (isAppSection && checkSectionScrollability(currentSection)) {
          const currentSectionElement = document.getElementById(sections[currentSection]);
          if (currentSectionElement) {
            const isAtBottom = (window.innerHeight + window.pageYOffset) >= (currentSectionElement.offsetTop + currentSectionElement.offsetHeight - 10);
            
            // Only prevent default and change sections if at the bottom
            if (isAtBottom) {
              e.preventDefault();
              nextSection = currentSection + 1;
            } else {
              return; // Allow normal scrolling within app section
            }
          }
        } else {
          e.preventDefault();
          nextSection = currentSection + 1;
        }
        break;
      case 'ArrowUp':
      case 'PageUp':
        // In the last section, allow normal page up behavior unless at top
        if (isLastSection) {
          const currentSectionElement = document.getElementById(sections[currentSection]);
          if (currentSectionElement) {
            const rect = currentSectionElement.getBoundingClientRect();
            const isAtTop = rect.top >= -10; // Small tolerance
            
            // Only change sections if at the very top
            if (isAtTop) {
              e.preventDefault();
              nextSection = currentSection - 1;
            } else {
              return; // Allow normal scrolling within testimonials
            }
          }
        } 
        // In the app section on mobile, allow normal page up behavior if content overflows
        else if (isAppSection && checkSectionScrollability(currentSection)) {
          const currentSectionElement = document.getElementById(sections[currentSection]);
          if (currentSectionElement) {
            const rect = currentSectionElement.getBoundingClientRect();
            const isAtTop = rect.top >= -10; // Small tolerance
            
            // Only change sections if at the very top
            if (isAtTop) {
              e.preventDefault();
              nextSection = currentSection - 1;
            } else {
              return; // Allow normal scrolling within app section
            }
          }
        } else {
          e.preventDefault();
          nextSection = currentSection - 1;
        }
        break;
      case 'Home':
        e.preventDefault();
        nextSection = 0;
        break;
      case 'End':
        e.preventDefault();
        nextSection = sections.length - 1;
        break;
      default:
        return;
    }
    
    if (nextSection >= 0 && nextSection < sections.length && nextSection !== currentSection) {
      scrollToSection(nextSection);
    }
  }, [currentSection, isScrolling, scrollToSection, sections, checkSectionScrollability]);

  // Initialize and detect current section on mount
  const detectCurrentSection = useCallback(() => {
    if (isScrolling) return;
    
    const scrollPosition = window.pageYOffset;
    const windowHeight = window.innerHeight;
    
    for (let i = 0; i < sections.length; i++) {
      const section = document.getElementById(sections[i]);
      if (section) {
        const rect = section.getBoundingClientRect();
        const sectionTop = scrollPosition + rect.top;
        const sectionBottom = sectionTop + rect.height;
        
        // Special handling for the last section (testimonials)
        if (i === sections.length - 1) {
          // We're in the testimonials section if any part of it is visible and we're past the previous section
          if (rect.top <= windowHeight && rect.bottom >= 0) {
            if (currentSection !== i) {
              setCurrentSection(i);
            }
            break;
          }
        } else {
          // For other sections, use the center-based detection
          if (scrollPosition + windowHeight / 2 >= sectionTop && 
              scrollPosition + windowHeight / 2 <= sectionBottom) {
            if (currentSection !== i) {
              setCurrentSection(i);
            }
            break;
          }
        }
      }
    }
  }, [sections, currentSection, isScrolling]);

  useEffect(() => {
    // Detect initial section
    setTimeout(detectCurrentSection, 100);
    
    // Add scroll listener for section detection (passive for performance)
    const handleScroll = () => {
      // Use requestAnimationFrame to throttle the detection
      requestAnimationFrame(detectCurrentSection);
    };
    
    // Add event listeners
    const options: AddEventListenerOptions = { passive: false };
    const passiveOptions: AddEventListenerOptions = { passive: true };
    
    document.addEventListener('wheel', handleWheel, options);
    document.addEventListener('touchstart', handleTouchStart, options);
    document.addEventListener('touchmove', handleTouchMove, options);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, passiveOptions);
    
    // Clean up
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleWheel, handleTouchStart, handleTouchMove, handleKeyDown, detectCurrentSection]);

  return {
    currentSection,
    isScrolling,
    scrollToSection: (index: number) => scrollToSection(index, true),
    goToNextSection: () => scrollToSection(currentSection + 1),
    goToPreviousSection: () => scrollToSection(currentSection - 1),
  };
};
